import React, { useState, useEffect, memo } from 'react';
import classNames from 'classnames';
import { Map, List } from 'immutable';
import PropTypes from 'prop-types';

import Popup from '../../helpers/Popup';
import Button from '../../helpers/Button';
import FormGroup from '../../helpers/FormGroup';
import FormErrors from '../../helpers/FormErrors';
import config from '../../../config';
import { InputProps } from '../../helpers/AutoComplete';
import AutoComplete from '../../../containers/AutoComplete';
import downloadFile from '../../../utils/downloadFile';
import PrettyError from '../../decorators/PrettyError';
import DisplayError from '../../decorators/DisplayError';
import BaseSelect from '../../helpers/Select';
import { usePopupContext } from '../../../contexts/PopupContext';

import multiselectItemStyle from '../../helpers/MultiselectItem/style.module.css';
import style from './style.module.css';

const Select = PrettyError(DisplayError(BaseSelect));

/** Conflict report popup component. */
const ConflictReportPopup = props => {
  const { closePopup } = usePopupContext();
  const { companyInfoData, statuses, findUsers, findDirectors, clearUsers } = props;

  const [lowestStatus, setLowestStatus] = useState('1.00');
  const [highestStatus, setHighestStatus] = useState('10.00');
  const [include, setInclude] = useState('all');
  const [selectedInclude, setSelectedInclude] = useState([]);
  const [suggestValue, setSuggestValue] = useState('');
  const [activity, setActivity] = useState('active');
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    setFormError(null);
  }, [include, activity]);

  /**
   * Form submit handler.
   *
   * @param {SyntheticEvent} event The react `SyntheticEvent`.
   */
  const handleSubmit = event => {
    event.preventDefault();

    if (isFormValid()) {
      const url = config.main.get('conflictReport');

      const selectedQuery =
        selectedInclude.length > 0 ? `&${selectedInclude.map(item => `selected[]=${item.id}`).join('&')}` : '';

      downloadFile({
        url: `${url}?include=${include}&activity=${activity}&status_range[low]=${lowestStatus}&status_range[high]=${highestStatus}${selectedQuery}`,
      });

      closePopup();
    }
  };

  /** Check form is valid or not. */
  const isFormValid = () => {
    setFormError(null);

    if ((include === 'lead' || include === 'team') && selectedInclude.length === 0) {
      setFormError('Please select individuals');

      return false;
    }

    return true;
  };

  /**
   * Handle lowest status change.
   *
   * @param {SyntheticEvent} event Select change event.
   */
  const handleLowestStatusChange = event => {
    const { value } = event.target;

    setLowestStatus(value);

    if (Number(value) > Number(highestStatus)) {
      setHighestStatus(value);
    }
  };

  /**
   * Handle highest status change.
   *
   * @param {SyntheticEvent} event Select change event.
   */
  const handleHighestStatusChange = event => {
    const { value } = event.target;

    setHighestStatus(value);

    if (Number(value) < Number(lowestStatus)) {
      setLowestStatus(value);
    }
  };

  /**
   * Handle "Include" radio input change.
   *
   * @param {SyntheticEvent} event Input change event.
   */
  const handleIncludeChange = event => {
    setInclude(event.target.value);
    setSuggestValue('');
    setSelectedInclude([]);
    clearUsers();
  };

  /**
   * Handle "Activity" radio input change.
   *
   * @param {SyntheticEvent} event Input change event.
   */
  const handleActivityChange = event => {
    setActivity(event.target.value);
  };

  /**
   * Check "Activity" field on click with the same value.
   *
   * @param {SyntheticEvent} event Input click event.
   */
  const handleActivitySameClick = event => {
    const newActivity = event.target.value;

    if (newActivity === activity) {
      setActivity('inactive');
    }
  };

  /**
   * Handle suggests changed.
   *
   * @param {object} payload Autocomplete payload.
   * @param {string} payload.name Suggest field name.
   * @param {number} payload.value Suggest field value.
   */
  const handleSuggestChange = ({ name, value }) => {
    if (name === 'suggestText') {
      setSuggestValue(value);
    }
  };

  /**
   * Handle suggests selected.
   *
   * @param {object} payload Autocomplete select payload.
   * @param {number} payload.id Suggest id.
   * @param {string} payload.text Suggest field value text.
   */
  const handleSuggestSelect = ({ id, text }) => {
    const isSameInclude = selectedInclude.find(include => include.id === id);

    if (isSameInclude === undefined) {
      setSelectedInclude([...selectedInclude, { id, name: text }]);
    }
    setSuggestValue('');
  };

  /**
   * Handle suggests deleted.
   *
   * @param {SyntheticEvent} event Button click event.
   */
  const handleRemoveSelectedInclude = event => {
    const { name } = event.target;
    const id = parseInt(name.replace('include_', ''), 10);

    setSelectedInclude(selectedInclude.filter(item => item.id !== id));
  };

  const header = 'Download Conflict Report';
  const footer = (
    <Button className="btn-primary" onClick={handleSubmit} type="button">
      Download
    </Button>
  );

  const renderSelectedInclude =
    selectedInclude.length > 0 &&
    selectedInclude.map(include => (
      <div key={include.id} className={multiselectItemStyle.host}>
        {include.name}
        <button
          aria-label="close"
          className={classNames(multiselectItemStyle.close, 'close')}
          name={`include_${include.id}`}
          onClick={handleRemoveSelectedInclude}
          type="button"
        >
          &times;
        </button>
      </div>
    ));

  const renderDirectors = include === 'lead' && (
    <>
      <FormGroup className="w260 mb0">
        <InputProps className="form-control" placeholder="Lead Director">
          <AutoComplete
            highlightFirst={false}
            onChange={handleSuggestChange}
            onFind={findDirectors}
            onSelectCallback={handleSuggestSelect}
            suggestions={companyInfoData.get('suggests')}
            value={suggestValue}
            valueName="suggestText"
          />
        </InputProps>
      </FormGroup>
      <div className="d-flex flex-wrap">{renderSelectedInclude}</div>
    </>
  );

  const renderUsers = include === 'team' && (
    <>
      <FormGroup className="w260 mb0">
        <InputProps className="form-control" placeholder="User">
          <AutoComplete
            highlightFirst={false}
            onChange={handleSuggestChange}
            onFind={findUsers}
            onSelectCallback={handleSuggestSelect}
            suggestions={companyInfoData.get('suggests')}
            value={suggestValue}
            valueName="suggestText"
          />
        </InputProps>
      </FormGroup>
      <div className="d-flex flex-wrap">{renderSelectedInclude}</div>
    </>
  );

  return (
    <Popup className={style.host} footer={footer} header={header} id="ConflictReportPopup">
      <p className="form-section-label">Current Buyer Status</p>
      <form onSubmit={handleSubmit}>
        <div className="row">
          <FormGroup className="col-xs-6">
            <Select
              id="lowestStatus"
              label="Lowest"
              name="lowestStatus"
              onChange={handleLowestStatusChange}
              options={statuses}
              selectClassName="input-sm"
              value={lowestStatus}
              showLabel
              showValue
            />
          </FormGroup>
          <FormGroup className="col-xs-6">
            <Select
              id="highestStatus"
              label="Highest"
              name="highestStatus"
              onChange={handleHighestStatusChange}
              options={statuses}
              selectClassName="input-sm"
              value={highestStatus}
              showLabel
              showValue
            />
          </FormGroup>
        </div>

        <p className="form-section-label">Individuals Included</p>
        <FormGroup>
          <div className="radio-wrap radio-wrap--reverse">
            <label htmlFor="includeDirectors">Lead Directors</label>
            <input
              checked={include === 'lead'}
              id="includeDirectors"
              name="include"
              onChange={handleIncludeChange}
              type="radio"
              value="lead"
            />
          </div>
          {renderDirectors}
        </FormGroup>
        <FormGroup>
          <div className="radio-wrap radio-wrap--reverse">
            <label htmlFor="includeUsers">Project team / Users</label>
            <input
              checked={include === 'team'}
              id="includeUsers"
              name="include"
              onChange={handleIncludeChange}
              type="radio"
              value="team"
            />
          </div>
          {renderUsers}
        </FormGroup>
        <FormGroup>
          <div className="radio-wrap radio-wrap--reverse">
            <label htmlFor="includeAll">All</label>
            <input
              checked={include === 'all'}
              id="includeAll"
              name="include"
              onChange={handleIncludeChange}
              type="radio"
              value="all"
            />
          </div>
        </FormGroup>

        <p className="form-section-label">Active/Inactive Projects</p>

        <FormGroup>
          <div className="radio-wrap radio-wrap--reverse">
            <label htmlFor="activityActive">Active: Only include &quot;Active&quot; Projects</label>
            <input
              checked={activity === 'active'}
              id="activityActive"
              name="activity"
              onChange={handleActivityChange}
              onClick={handleActivitySameClick}
              type="radio"
              value="active"
            />
          </div>
        </FormGroup>
        <FormGroup>
          <div className="radio-wrap radio-wrap--reverse radio-wrap--start">
            <label htmlFor="activityAll">
              All: Include both &quot;Active&quot; and &quot;Inactive&quot; Projects where activity occurred during the
              date range provided
            </label>
            <input
              checked={activity === 'all'}
              id="activityAll"
              name="activity"
              onChange={handleActivityChange}
              onClick={handleActivitySameClick}
              type="radio"
              value="all"
            />
          </div>
        </FormGroup>
        <FormErrors className="block-center mb0 mt15 error-text" errors={formError} />
      </form>
    </Popup>
  );
};

ConflictReportPopup.propTypes = {
  clearUsers: PropTypes.func.isRequired,
  companyInfoData: PropTypes.instanceOf(Map).isRequired,
  findDirectors: PropTypes.func.isRequired,
  findUsers: PropTypes.func.isRequired,
  statuses: PropTypes.instanceOf(List).isRequired,
};

export default memo(ConflictReportPopup);
